import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {Loader, LoaderVariantsEnum} from '../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {ThemeReader, useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {GridAreaItem} from '../../components/common/grid';
import {SettingsBannersEnum} from './settingsBannersContext';

/*
 * Props.
 */

interface SettingsBannerProps {
  type: SettingsBannersEnum;
  onDismissClick: Handler;
}

/*
 * Style.
 */

interface BannerConstants {
  findIconColor: ThemeReader;
  findBackgroundColor: ThemeReader;
  icon: IconName;
}
const bannerConstants: {[k in SettingsBannersEnum]: BannerConstants} = {
  [SettingsBannersEnum.INFO]: {
    findBackgroundColor: (theme) => theme.palette.blue.shade20,
    findIconColor: (theme) => theme.palette.blue.shade40,
    icon: 'infoFilledSmall',
  },
  [SettingsBannersEnum.LOADING]: {
    findBackgroundColor: (theme) => theme.greys.shade40,
    findIconColor: (theme) => theme.greys.shade40,
    icon: 'infoFilledSmall',
  },
  [SettingsBannersEnum.SUCCESS]: {
    findBackgroundColor: (theme) => theme.palette.green.shade20,
    findIconColor: (theme) => theme.palette.green.shade40,
    icon: 'checkmarkCircleSmall',
  },
  [SettingsBannersEnum.ERROR]: {
    findBackgroundColor: (theme) => theme.palette.red.shade20,
    findIconColor: (theme) => theme.palette.red.shade40,
    icon: 'warningFilledSmall',
  },
  [SettingsBannersEnum.WARNING]: {
    findBackgroundColor: (theme) => theme.palette.orange.shade20,
    findIconColor: (theme) => theme.palette.orange.shade40,
    icon: 'warningFilledSmall',
  },
};

const StyledDiv = styled.div<{$styles: BannerConstants}>`
  border-radius: 18px;
  ${(p) => boxShadow(p.theme, `0px 2px 10px rgba(0, 0, 0, 0.2)`)};
  border-radius: 18px;
  padding: 0;
  height: 34px;
  left: 50%;
  top: 0;
  position: absolute;
  transform: translateX(-50%);

  display: inline-grid;
  grid-template-areas: '. icon . text . close .';
  grid-template-columns: 12px 16px 10px auto 4px auto 4px;
  align-items: center;

  ${(p) => css`
    background-color: ${p.$styles.findBackgroundColor(p.theme)};
  `}
`;

const StyledTextDiv = styled.div`
  grid-area: text;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 1;
`;

/*
 * Component.
 */

export const SettingsBanner: FC<SettingsBannerProps> = (props) => {
  const theme = useUserTheme();
  const bannerStyles = bannerConstants[props.type];

  return (
    <StyledDiv $styles={bannerStyles}>
      <GridAreaItem area="icon">
        {props.type === SettingsBannersEnum.LOADING ? (
          <Loader size={VisualSizesEnum.SMALL} variant={LoaderVariantsEnum.GREY} />
        ) : (
          <Icon name={bannerStyles.icon} color={bannerStyles.findIconColor(theme)} />
        )}
      </GridAreaItem>
      <StyledTextDiv>{props.children}</StyledTextDiv>
      <GridAreaItem area="close">
        <IconButton
          isRounded
          hoverColor={theme.greys.shade80}
          color={theme.greys.shade70}
          renderTooltip={() => null}
          label={<Trans id="settingsbanner-dismiss">Dismissed</Trans>}
          iconName="closeSmall"
          onClick={props.onDismissClick}
          interactionId="dismiss-settings-banner"
        />
      </GridAreaItem>
    </StyledDiv>
  );
};
